import { useMemo } from "react";
import {
  PurchaseRequestItemV2PaginatedResponse,
  PurchaseRequestV2PaginatedResponse,
  UserCompanyType,
} from "../../types";
import {
  useBuyerItemsQueueV2,
  useCompanyPurchaseRequestsV2,
  useMyPurchaseRequestsV2,
  useRequesterPurchaseRequestsV2,
  useFilterByTags,
  useFilterItemsByTags,
  useFilterItemsByCategories,
  useCompanyItemsV2,
} from "../../hooks/usePurchaseRequestsV2";
import { useUsersByCompany } from "../../hooks/useUsers";
import { useUser } from "../../context";
import { BuyerViewMode } from "./purchaseRequestListTypes";

interface UsePurchaseRequestListDataParams {
  page: number;
  size: number;
  statusFilter: string;
  buyerViewMode: BuyerViewMode;
  isRequester: boolean;
  isBuyerView: boolean;
  selectedBuyerId: string;
  selectedTagIds: string[];
  selectedCategoryIds: string[];
  selectedCompanyIds: string[];
  requestingSiteIdFilter: string;
}

/**
 * The data-orchestration "brain" behind the PR list screen (Task 4 Carbon migration), extracted
 * verbatim out of `PurchaseRequestsV2.tsx`'s own pre-Carbon hook soup so the container stays
 * under the file's line budget. Nothing here changes which endpoint is called for which
 * view/filter combination — same source-priority rules the pre-Carbon screen had (tag filter
 * beats category filter beats the view's own default query).
 */
export const usePurchaseRequestListData = ({
  page,
  size,
  statusFilter,
  buyerViewMode,
  isRequester,
  isBuyerView,
  selectedBuyerId,
  selectedTagIds,
  selectedCategoryIds,
  selectedCompanyIds,
  requestingSiteIdFilter,
}: UsePurchaseRequestListDataParams) => {
  const { user, isBuyer, isManager, isAnalyst } = useUser();

  const shouldFetchRequesterRequests = isRequester && !isBuyerView;
  const shouldFetchBuyerItems =
    isBuyerView && buyerViewMode === BuyerViewMode.ITEMS;
  const shouldFetchBuyerMyRequests =
    isBuyerView && buyerViewMode === BuyerViewMode.MY_REQUESTS;
  const shouldFetchCompanyRequests =
    isBuyerView && buyerViewMode === BuyerViewMode.REQUESTS;

  const {
    data: requesterRequests,
    isLoading: isLoadingRequester,
    isError: isErrorRequester,
  } = useRequesterPurchaseRequestsV2(
    page,
    statusFilter,
    shouldFetchRequesterRequests,
    size
  );

  const {
    data: myRequests,
    isLoading: isLoadingMy,
    isError: isErrorMy,
  } = useMyPurchaseRequestsV2(
    page,
    statusFilter,
    shouldFetchBuyerMyRequests,
    size
  );

  const { data: companyUsers } = useUsersByCompany(user?.id || "");

  // Filter to show only buyers/managers/analysts, including the logged-in user.
  const buyerUsers = useMemo(() => {
    if (!companyUsers) return [];
    const filteredUsers = companyUsers.filter(
      (u) =>
        u.userCompanyType === UserCompanyType.BUYER ||
        u.userCompanyType === UserCompanyType.MANAGER ||
        u.userCompanyType === UserCompanyType.ANALYST
    );
    const hasLoggedInUser = filteredUsers.some((u) => u.id === user?.id);
    if (user && !hasLoggedInUser && (isBuyer || isManager || isAnalyst)) {
      filteredUsers.push(user);
    }
    return filteredUsers;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyUsers, user, isBuyer, isManager, isAnalyst]);

  const allBuyerIds = useMemo(() => buyerUsers.map((u) => u.id), [buyerUsers]);

  const buyerIdFilter = selectedBuyerId ? selectedBuyerId : undefined;

  const shouldFetchAllBuyersItems =
    shouldFetchBuyerItems && selectedBuyerId === "" && allBuyerIds.length > 0;

  const {
    data: companyRequests,
    isLoading: isLoadingCompany,
    isError: isErrorCompany,
  } = useCompanyPurchaseRequestsV2(
    page,
    statusFilter,
    buyerIdFilter,
    shouldFetchCompanyRequests,
    size
  );

  const {
    data: singleBuyerItems,
    isLoading: isLoadingSingleBuyerItems,
    isError: isErrorSingleBuyerItems,
  } = useBuyerItemsQueueV2(
    page,
    statusFilter,
    buyerIdFilter,
    shouldFetchBuyerItems && selectedBuyerId !== "",
    size
  );

  const {
    data: allBuyersItems,
    isLoading: isLoadingAllBuyersItems,
    isError: isErrorAllBuyersItems,
  } = useCompanyItemsV2(
    page,
    allBuyerIds,
    statusFilter,
    shouldFetchAllBuyersItems || selectedCompanyIds.length > 0,
    selectedCompanyIds,
    requestingSiteIdFilter || undefined,
    size
  );

  const buyerItems = selectedBuyerId === "" ? allBuyersItems : singleBuyerItems;
  const isLoadingBuyerItems =
    selectedBuyerId === ""
      ? isLoadingAllBuyersItems
      : isLoadingSingleBuyerItems;
  const isErrorBuyerItems =
    selectedBuyerId === "" ? isErrorAllBuyersItems : isErrorSingleBuyerItems;

  const useTagFilter = selectedTagIds.length > 0;
  const { data: tagFilteredRequests, isLoading: isLoadingTagRequests } =
    useFilterByTags({
      page,
      tagIds: selectedTagIds,
      buyerId: buyerIdFilter,
      size,
    });

  const { data: tagFilteredItems, isLoading: isLoadingTagItems } =
    useFilterItemsByTags({
      page,
      tagIds: selectedTagIds,
      buyerId: buyerIdFilter,
      size,
    });

  const useCategoryFilter = selectedCategoryIds.length > 0;
  const { data: categoryFilteredItems, isLoading: isLoadingCategoryItems } =
    useFilterItemsByCategories({
      page,
      categoryIds: selectedCategoryIds,
      buyerId: buyerIdFilter,
      size,
    });

  const currentRequests: PurchaseRequestV2PaginatedResponse | undefined =
    useMemo(() => {
      if (useTagFilter && buyerViewMode !== BuyerViewMode.ITEMS) {
        return tagFilteredRequests;
      }
      if (isBuyerView) {
        if (buyerViewMode === BuyerViewMode.MY_REQUESTS) return myRequests;
        if (buyerViewMode === BuyerViewMode.REQUESTS) return companyRequests;
        return undefined;
      }
      return requesterRequests;
    }, [
      useTagFilter,
      tagFilteredRequests,
      buyerViewMode,
      companyRequests,
      isBuyerView,
      myRequests,
      requesterRequests,
    ]);

  const filteredContent =
    currentRequests?.content.filter((request) =>
      statusFilter === "ALL" ? true : request.overallStatus === statusFilter
    ) || [];

  const currentItems: PurchaseRequestItemV2PaginatedResponse | undefined =
    useMemo(() => {
      if (buyerViewMode !== BuyerViewMode.ITEMS) return undefined;
      if (useTagFilter) return tagFilteredItems;
      if (useCategoryFilter) return categoryFilteredItems;
      return buyerItems;
    }, [
      buyerViewMode,
      useTagFilter,
      tagFilteredItems,
      useCategoryFilter,
      categoryFilteredItems,
      buyerItems,
    ]);

  const filteredItems =
    currentItems?.content.filter((item) =>
      statusFilter === "ALL" ? true : item.state === statusFilter
    ) || [];

  const isLoading = Boolean(
    (shouldFetchBuyerItems && isLoadingBuyerItems) ||
      (shouldFetchBuyerMyRequests && isLoadingMy) ||
      (shouldFetchCompanyRequests && isLoadingCompany) ||
      (shouldFetchRequesterRequests && isLoadingRequester) ||
      (useTagFilter &&
        buyerViewMode === BuyerViewMode.ITEMS &&
        isLoadingTagItems) ||
      (useTagFilter &&
        buyerViewMode !== BuyerViewMode.ITEMS &&
        isLoadingTagRequests) ||
      (useCategoryFilter &&
        buyerViewMode === BuyerViewMode.ITEMS &&
        isLoadingCategoryItems)
  );

  const isError = Boolean(
    (shouldFetchBuyerItems && isErrorBuyerItems) ||
      (shouldFetchBuyerMyRequests && isErrorMy) ||
      (shouldFetchCompanyRequests && isErrorCompany) ||
      (shouldFetchRequesterRequests && isErrorRequester)
  );

  return {
    currentRequests,
    filteredContent,
    currentItems,
    filteredItems,
    isLoading,
    isError,
    buyerUsers,
  };
};
